import { ImageZoom } from '../components/ImageZoom';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { analyticsAPI, questionsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { SUBJECTS, SUBJECT_COLORS, DIFFICULTY_COLORS, formatDate, cn } from '../lib/utils';
import {
    XCircle,
    CheckCircle,
    BookOpen,
    Filter,
    RefreshCw
} from 'lucide-react';
import Layout from '../components/Layout';

export default function MissedQuestions() {
    const navigate = useNavigate();
    const [missedQuestions, setMissedQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterSubject, setFilterSubject] = useState('all');

    useEffect(() => {
        fetchMissedQuestions();
    }, [filterSubject]);

    const fetchMissedQuestions = async () => {
        try {
            setLoading(true);
            const subject = filterSubject === 'all' ? null : filterSubject;
            const response = await analyticsAPI.getMissedQuestions(subject, 50);
            setMissedQuestions(response.data.missed_questions);
        } catch (err) {
            console.error('Failed to fetch missed questions:', err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="space-y-6">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-12 w-64" />
                    <div className="space-y-4">
                        {[1, 2, 3].map(i => (
                            <Skeleton key={i} className="h-32" />
                        ))}
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in" data-testid="missed-questions-container">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                            Missed Questions
                        </h1>
                        <p className="text-slate-500 mt-1">Review your incorrect answers and learn from mistakes</p>
                    </div>
                    <Button 
                        variant="outline" 
                        onClick={fetchMissedQuestions}
                        data-testid="refresh-missed-btn"
                    >
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Refresh
                    </Button>
                </div>

                {/* Filter */}
                <Card className="border-slate-200">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                            <Filter className="h-5 w-5 text-slate-400" />
                            <div className="flex-1 max-w-xs">
                                <Select value={filterSubject} onValueChange={setFilterSubject}>
                                    <SelectTrigger data-testid="filter-subject-select">
                                        <SelectValue placeholder="Filter by subject" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Subjects</SelectItem>
                                        {SUBJECTS.map(s => (
                                            <SelectItem key={s} value={s}>{s}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <span className="text-sm text-slate-500">
                                {missedQuestions.length} questions found
                            </span>
                        </div>
                    </CardContent>
                </Card>

                {/* Questions List */}
                {missedQuestions.length > 0 ? (
                    <Accordion type="single" collapsible className="space-y-4">
                        {missedQuestions.map((item, index) => {
                            const { attempt, question } = item;
                            const subjectColors = SUBJECT_COLORS[question.subject];
                            const difficultyColors = DIFFICULTY_COLORS[question.difficulty];

                            return (
                                <AccordionItem 
                                    key={attempt.id} 
                                    value={attempt.id}
                                    className="border border-slate-200 rounded-xl overflow-hidden bg-white"
                                >
                                    <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-slate-50">
                                        <div className="flex items-start gap-4 text-left w-full">
                                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                                                <XCircle className="h-5 w-5 text-red-500" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Badge className={cn(subjectColors.bg, subjectColors.text, subjectColors.border, "text-xs")}>
                                                        {question.subject}
                                                    </Badge>
                                                    <Badge className={cn(difficultyColors.bg, difficultyColors.text, "text-xs")}>
                                                        {question.difficulty}
                                                    </Badge>
                                                </div>
                                                <p className="text-sm text-slate-700 line-clamp-2">
                                                    {question.question_stem}
                                                </p>
                                                <p className="text-xs text-slate-500 mt-1">
                                                    Attempted: {formatDate(attempt.created_at)}
                                                </p>
                                            </div>
                                        </div>
                                    </AccordionTrigger>
                                    <AccordionContent className="px-6 pb-6">
                                        <div className="space-y-4 pt-4 border-t border-slate-100">
                                            {/* Medical Image with description (shown in review) */}
                                            {question.image_base64 && (
                                                <div>
                                                    <div className="rounded-lg overflow-hidden border-2 border-slate-300 bg-white">
                                                        <ImageZoom 
                                                            src={`data:image/png;base64,${question.image_base64}`}
                                                            alt="Medical image"
                                                        />
                                                    </div>
                                                    {question.image_description && (
                                                        <div className="mt-2 p-2 rounded bg-indigo-50 border border-indigo-200">
                                                            <p className="text-xs text-indigo-700">
                                                                <span className="font-medium">Image:</span> {question.image_description}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Vignette */}
                                            <div className="p-4 rounded-lg bg-slate-50">
                                                <p className="text-sm text-slate-600 leading-relaxed">
                                                    {question.vignette}
                                                </p>
                                            </div>

                                            {/* Question */}
                                            <p className="font-medium text-slate-900">
                                                {question.question_stem}
                                            </p>

                                            {/* Options */}
                                            <div className="space-y-2">
                                                {question.options.map((option) => {
                                                    const isCorrect = option.letter === question.correct_answer;
                                                    const wasSelected = option.letter === attempt.selected_answer;

                                                    return (
                                                        <div
                                                            key={option.letter}
                                                            className={cn(
                                                                "flex items-start gap-3 p-3 rounded-lg border-2",
                                                                isCorrect && "border-emerald-300 bg-emerald-50",
                                                                wasSelected && !isCorrect && "border-red-300 bg-red-50",
                                                                !isCorrect && !wasSelected && "border-slate-200 bg-white"
                                                            )}
                                                        >
                                                            <div className={cn(
                                                                "flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-medium",
                                                                isCorrect && "bg-emerald-600 text-white",
                                                                wasSelected && !isCorrect && "bg-red-600 text-white",
                                                                !isCorrect && !wasSelected && "bg-slate-200 text-slate-600"
                                                            )}>
                                                                {isCorrect ? (
                                                                    <CheckCircle className="h-4 w-4" />
                                                                ) : wasSelected ? (
                                                                    <XCircle className="h-4 w-4" />
                                                                ) : (
                                                                    option.letter
                                                                )}
                                                            </div>
                                                            <span className={cn(
                                                                "text-sm",
                                                                isCorrect && "text-emerald-800 font-medium",
                                                                wasSelected && !isCorrect && "text-red-800"
                                                            )}>
                                                                {option.text}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            {/* Explanation */}
                                            <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                                                <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                                                    <BookOpen className="h-4 w-4" />
                                                    Explanation
                                                </h4>
                                                <p className="text-sm text-amber-900 leading-relaxed">
                                                    {question.explanation}
                                                </p>
                                            </div>

                                            {/* Your Answer Summary */}
                                            <div className="flex items-center gap-4 text-sm">
                                                <span className="text-slate-500">Your answer:</span>
                                                <span className="font-medium text-red-600">{attempt.selected_answer}</span>
                                                <span className="text-slate-500">Correct answer:</span>
                                                <span className="font-medium text-emerald-600">{attempt.correct_answer}</span>
                                            </div>
                                        </div>
                                    </AccordionContent>
                                </AccordionItem>
                            );
                        })}
                    </Accordion>
                ) : (
                    <Card className="border-slate-200 border-dashed">
                        <CardContent className="p-12">
                            <div className="text-center">
                                <CheckCircle className="h-16 w-16 mx-auto mb-4 text-emerald-400" />
                                <h3 className="text-lg font-semibold text-slate-700 mb-2" style={{ fontFamily: 'Manrope' }}>
                                    No Missed Questions
                                </h3>
                                <p className="text-slate-500 mb-6 max-w-md mx-auto">
                                    {filterSubject === 'all' 
                                        ? "You haven't missed any questions yet. Keep up the great work!"
                                        : `You haven't missed any questions in ${filterSubject}.`}
                                </p>
                                <Button onClick={() => navigate('/practice')} data-testid="go-practice-btn">
                                    Continue Practicing
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </Layout>
    );
}
